import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, from, ReplaySubject, Subject, Subscription, tap, toArray } from 'rxjs';
import { MainService } from '../main/main.service';
import dexfreight, { Microservices } from '../../microservices';
import { Notification } from 'src/app/@types/Notification.type';
import { DrayageService } from '../back-services/drayage.service';
import { NotificationsService } from '../notification/notifications.service';
import { NotificationType } from 'src/app/@enums/NotificationType.enum';
import { NotificationTag } from 'src/app/@enums/NotificationTag.enum';

@Injectable()
export class ObservablesService {

    subscriptions: Subscription[] = [];
    // FTL OBS
    carrierShip: Subject<any> = new Subject();
    myShipments: BehaviorSubject<any> = new BehaviorSubject(null);
    availableShipments: ReplaySubject<any> = null;
    newShipments: ReplaySubject<any> = null;
    notifications: ReplaySubject<Notification> = null;
    //DRAYAGE OBS
    drayageShipments: ReplaySubject<any> = null;
    myDrayages: ReplaySubject<any> = null;
    drayageAvailableShipments: ReplaySubject<any> = null;
    private drayageShipments$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    //PREMIUM
    tours = []
    myLoads: boolean = true;

    constructor(
        public micro: Microservices,
        private ms: MainService,
        private drayageService: DrayageService,
        private notificationsService: NotificationsService
    ) {
        this.notifications = new ReplaySubject();
        this.notificationsService.notifications.subscribe(notification => {
            this.notifications.next(notification);
        });

        this.notifications.subscribe(this.manageNotification.bind(this));
    }

    setupListeners() {
        this.subscriptions.push(
            this.drayageService.feed.pipe(
                tap((load) => this.setDrayageList([load, ...this.drayageShipments$.value])),
            ).subscribe()
        );
    }

    get drayageList() {
        return this.drayageShipments$;
    }

    setDrayageList(drayages: any[]) {
        this.drayageShipments$.next(Array.isArray(drayages) ? drayages : []);
    }

    deleteDrayagesFromObservable(drayageIds: string[]) {
        from(this.drayageShipments$.value).pipe(
            filter((load: any) => !drayageIds.includes(load._id)),
            toArray(),
            tap((drayages) => this.setDrayageList([...drayages])),
        ).subscribe()
    }

    async initNewShipments(_token?, _filter?) { /* no-op */ }

    async initNotifications(token) {
        if (!this.notifications)
            this.notifications = new ReplaySubject();

        try {
            const notificationSubs = this.notifications
                .subscribe(async notif => {
                    if (notif && !notif.tags.includes('drayage') && notif.data.shipment) {
                        await this.updateShipment(notif.data.shipment);
                    } else {
                        if (notif.data.uniqueId) await this.updateDrayage(notif.data.uniqueId);
                    }

                });
            this.subscriptions.push(notificationSubs);
        } catch (error) {
            throw error;
        }
    }
    async updateShipment(_shipment: string) {
        // FTL shipment cache removed; this is now a no-op.
    }

    async updateDrayage(uniqueId: string) {
        try {
            let shipment = await dexfreight.Drayage.getByUniqueId(uniqueId, localStorage.token);

            //! ------------- MY SHIPMENTS --------------
            {
                if ((<any>this.drayageShipments)) {
                    const ships = (<any>this.drayageShipments)._events;
                    if (ships) {
                        const ship = (<any>this.drayageShipments)._events.find(s => s.uniqueId === uniqueId);
                        if (ship) {
                            const index = ships.indexOf(ship);
                            ships.splice(index, 1);
                            if (ships) {
                                ships.unshift(shipment);
                            }
                            return;
                        }
                    }
                }
            }
            //! ----------- AVAILABLE SHIPMENTS ---------
            {
                if ((<any>this.drayageAvailableShipments)) {
                    const ships = (<any>this.drayageAvailableShipments)._events;
                    if (ships) {
                        const ship = (<any>this.drayageAvailableShipments)._events.find(s => s._id === shipment);
                        if (ship) {
                            const index = ships.indexOf(shipment);

                            if (ship.state === "open") {
                                ships.splice(index, 1);
                                if (ships) {
                                    ships.unshift(shipment);
                                }
                            } else {
                                (<any>this.drayageAvailableShipments)._events.unshift(shipment);
                            }

                            return;
                        }
                    }
                }
            }
        } catch (error) {
            console.log(error);

        }

    }

    /**
     * Unsubscribe obs when logout
     */
    unsubscribe() {
        // this.myShipments = null;
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        })
    }


    async manageNotification(notification) {
        const { type, tags, data } = notification;
        switch (type) {
            case NotificationType.complementoPago:
                this.ms.listener.dispatchEvent('updatePaymentPluginPendings')
                this.ms.listener.dispatchEvent('updatePaymentPluginIssued')
                break;

            case NotificationType.invoiceConfiguration:
                await this.updateDrayage(data?.uniqueId || data?.shipment);
                this.ms.listener.dispatchEvent('updateInformation@NewDrayage');
                this.ms.listener.dispatchEvent('updateAllCarrierStatus');
                this.ms.listener.dispatchEvent('updateShipment@DrayageList', data?.uniqueId || data?.shipment)
                break;

            case NotificationType.documentsAccepted,
                NotificationType.driverChanged:
                this.ms.listener.dispatchEvent('updateShipmentDetails@drayage');
                if (type !== NotificationType.createDrayage) {
                    this.ms.listener.dispatchEvent('update@drayageList', { show: true });
                    this.ms.listener.dispatchEvent('updateShipmentDetails@drayage');
                }
                break;

            case NotificationType.trackingDrayage:
                this.ms.listener.dispatchEvent('trackingDrayage@drayage');
                break;

            case NotificationType.trackingDrayageSilent:
                this.ms.listener.dispatchEvent('trackingDrayageDelivered@drayage');
                break;

            case NotificationType.shipmentCreated:
                this.ms.listener.dispatchEvent('isInShipmentList@@', { show: true });
                break;

            case NotificationType.shipmentStageChange:
                this.ms.listener.dispatchEvent('updateTimeline@timeline', notification.data.shipment);
                if (notification.data.action == 'DELIVERED'
                    && localStorage.role === 'carrier') {
                    this.ms.listener.dispatchEvent('openDeliveryAlert@notification', notification);
                }
                break;

            case NotificationType.documentSignedRate,
                NotificationType.documentSigned:
                if (tags.includes(NotificationTag.drayage)) {
                    this.ms.listener.dispatchEvent('updateShipmentDetails@drayage');
                    if (type !== NotificationType.createDrayage) {
                        this.ms.listener.dispatchEvent('update@drayageList', { show: true });
                        this.ms.listener.dispatchEvent('updateShipmentDetails@drayage');
                    }
                } else {
                    this.ms.listener.dispatchEvent('getCertificates@shipmentDetails');
                }
                break;

            case NotificationType.documentsAcceptedDrayage:
                this.ms.listener.dispatchEvent('getDocsCompany@drayageDetails');
                break;

            case NotificationType.bidAccepted:
                setTimeout(() => {
                    this.updateShipment(data.shipmentId ? data.shipmentId : data.shipment)
                })
                break;

            case NotificationType.shipmentReadyForPickup:
                setTimeout(() => { this.ms.listener.dispatchEvent('getLoad@shipment/details'); }, 2000);
                break;

            case NotificationType.bidDeclined:
                this.ms.listener.dispatchEvent('activyLog@ShipDetails');
                break;

            case NotificationType.newAgreementUploaded,
                NotificationType.documentDeclined:
                if (notification.data.validation == 'ftl') {
                    this.ms.listener.dispatchEvent('getCertificates@shipmentDetails');
                    this.ms.listener.dispatchEvent('getLoad@shipment/details');
                } else {
                    this.ms.listener.dispatchEvent('getDocsCompany@drayageDetails');
                    this.ms.listener.dispatchEvent('updateShipmentDetails@drayage');
                }
                break;

            case NotificationType.quickbooksAuthUrl:
                window.open(notification.data.url, '', 'menubar=yes,location=yes,' +
                    'resizable=yes,scrollbars=yes,status=no, width=700, height=700')
                break;

            case NotificationType.informationEditDrayage:
                await this.updateDrayage(data.shipmentId);
                this.ms.listener.dispatchEvent('updateInformation@NewDrayage');
                break;

            case NotificationType.editedServicesPaymentTableDrayageDetails:
                await this.updateDrayage(data.shipmentId);
                this.ms.listener.dispatchEvent('updatePayment@NewDrayage');
                this.ms.listener.dispatchEvent('Re-GeneratedRate@NewDetails');
                break;

            default:

                break;
        }

        /**
         * @description All this notifications trigger the reorden on new details
         */
        if (['informationEditDrayageSilent', 'informationEditDrayage', 'documentSignedDrayage',
            'editServicesDrayages', 'acceptedServicesDrayages', 'agreementDrayage'].includes(type)) {
            this.ms.listener.dispatchEvent("orderSeccion@NewDrayage");
        }

        /**
         * @description Update Table of payment when user accepted the new charges
         */
        if (['acceptedServicesDrayages'].includes(type)) {
            await this.updateDrayage(data.shipmentId);
            this.ms.listener.dispatchEvent('updatePayment@NewDrayage');
            this.ms.listener.dispatchEvent('Re-GeneratedRate@NewDetails');
        }

        /**
         * @description Update load when user generate invoice.
         */
        if (['generateDrayageInvoice'].includes(type)) {
            this.ms.listener.dispatchEvent("updateFindPreInvoice@NewDrayage", notification?.data?.shipmentId);
            this.ms.listener.dispatchEvent("updateTransactionModule@NewDrayage");
            this.ms.listener.dispatchEvent("refreshTracking@Project");
            this.ms.listener.dispatchEvent("updateInformation@ProjectDetails");
        }

        /**
         * @description Refresh rate seccion when shipment information change
         */
        if ([NotificationType.informationEditDrayageSilent, NotificationType.informationEditDrayage].includes(type)) {
            await this.updateDrayage(data?.uniqueId);
            this.ms.listener.dispatchEvent('Re-GeneratedRate@NewDetails');
            this.ms.listener.dispatchEvent("RefreshRate@NewDetails");
            this.ms.listener.dispatchEvent('updateInformation@NewDrayage');
            this.ms.listener.dispatchEvent('updateShipment@DrayageList', notification?.data?.shipmentId)
        }
        /**
         * @description Update bids when negotiable
         */
        if ([NotificationType.auctionStartDrayage].includes(type)) {
            this.ms.listener.dispatchEvent('updateNegotiations@NewDrayage');
        }

        if ([NotificationType.driverAssigned, NotificationType.driverChanged, NotificationType.driverAssignedLog].includes(type)) {
            await this.updateDrayage(data?.uniqueId || data?.shipment);
            this.ms.listener.dispatchEvent('updateInformation@NewDrayage');
            this.ms.listener.dispatchEvent('updateAllCarrierStatus');
            this.ms.listener.dispatchEvent('updateShipment@DrayageList', data?.uniqueId || data?.shipment)

            this.ms.listener.dispatchEvent("refreshTracking@Project");
            this.ms.listener.dispatchEvent("updateInformation@ProjectDetails");
        }

        /**
        * @description Refresh documents/notification seccion and rate seccion when agreement has been signed
        */
        if ([NotificationType.documentSignedDrayage, NotificationType.agreementDrayage].includes(type)) {
            this.ms.listener.dispatchEvent("RefreshDocuments@NewDetails");
            this.ms.listener.dispatchEvent("RefreshCartaPorte");
            this.ms.listener.dispatchEvent("RefreshRate@NewDetails");
            this.ms.listener.dispatchEvent('getNotification@NewDrayage');
        }

        /**
         * @description Update user permission on details
         */
        if ([NotificationType.bidAcceptedDrayageLog].includes(type)) {
            await this.updateDrayage(data?.drayage);
            this.ms.listener.dispatchEvent("updateUsers");
            this.ms.listener.dispatchEvent('updateReferences@NewDrayage');
        }

        /**
         * @description Order the seccion in details when carrier assign driver. Update notifications seccion.
         */
        if ([NotificationType.driverAssignedLog].includes(type)) {
            this.ms.listener.dispatchEvent("orderSeccion@NewDrayage");
            this.ms.listener.dispatchEvent('getNotification@NewDrayage');
        }

        /**
         * @description Order when drayage is delivered.
         */
        if ([NotificationType.deliveredDrayage, NotificationType.trackingDrayageSilent].includes(type)) {
            if (NotificationType.trackingDrayageSilent === type) {
                await this.updateDrayage(data?._id);
            }
            this.ms.listener.dispatchEvent("orderSeccion@NewDrayage");
        }

        /**
         * @description Reload Rate
         */
        if (['acceptedServicesDrayages', 'editServicesDrayages'].includes(type)) {
            this.ms.listener.dispatchEvent('updateReferences@NewDrayage');
        }
        if (tags.includes(NotificationTag.blockchain)) {
            const [type, _id] = notification.data.id.split(":");

            if (type === NotificationType.shipment) {
                notification.data.shipment = _id;
            }
        }

        if (notification && notification.tags && notification.tags.includes(NotificationTag.extracharge)) {
            this.ms.listener.dispatchEvent('getLoad@shipment/details');
            this.ms.listener.dispatchEvent('updateExtraChargeDetails');
        }
    }
}
